import { useCallback, useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router";

import { reportUnexpectedError } from "~/api/client";
import { type DramaSeries, fetchDramaSeriesList } from "~/api/drama";
import { DramaCardLight } from "~/components/common/cinema/DramaCardLight";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "~/components/ui/carousel";
import { DramaCardLightSkeleton } from "~/components/skeleton/DramaCardLightSkeleton";
import { useUserStore } from "~/lib/stores/user";

const PLACEHOLDER_COUNT = 5;

/** The Cinema row on the home page. */
export function CinemaSection() {
  // A getter, not a subscription: the token is only needed at request time, and subscribing
  // fires the effect once before the session settles and again after.
  const readToken = useCallback(() => useUserStore.getState().token, []);
  const [series, setSeries] = useState<DramaSeries[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    fetchDramaSeriesList(readToken)
      .then(data => {
        if (!cancelled) setSeries(data.series ?? []);
      })
      .catch(error => {
        reportUnexpectedError(error);
        if (!cancelled) setSeries([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [readToken]);

  if (!isLoading && series.length === 0) return null;

  const header = (
    <h2 className="mb-4 flex items-center justify-between text-3xl font-bold tracking-tight">
      <span>Cinema</span>
      <Link
        id="home-cinema-see-all"
        to="/cinema"
        className="flex items-center gap-1 text-sm font-medium text-neutral-500 hover:text-neutral-700"
      >
        See all
        <ChevronRight className="size-3.5" />
      </Link>
    </h2>
  );

  if (isLoading) {
    return (
      <section id="home-cinema-section">
        {header}
        <div className="grid grid-cols-4 gap-2 md:grid-cols-5 md:gap-4">
          {Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => (
            <DramaCardLightSkeleton key={i} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section id="home-cinema-section">
      {header}
      <div className="group">
        <Carousel
          opts={{ align: "start", loop: false, dragFree: true, containScroll: "trimSnaps", duration: 20 }}
          className="relative w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {series.map(item => (
              <CarouselItem key={item.id} className="basis-1/4 pl-2 md:basis-1/5 md:pl-4">
                <DramaCardLight series={item} idPrefix="home-cinema-card" />
              </CarouselItem>
            ))}
          </CarouselContent>

          <CarouselPrevious
            variant="carouselNav"
            size="icon"
            customPosition={true}
            className="absolute top-[40%] -left-4 z-10 inline-flex size-8 -translate-y-1/2 disabled:hidden"
          />
          <CarouselNext
            variant="carouselNav"
            size="icon"
            customPosition={true}
            className="absolute top-[40%] -right-4 z-10 inline-flex size-8 -translate-y-1/2 disabled:hidden"
          />
        </Carousel>
      </div>
    </section>
  );
}
